import React, { useState, useEffect } from 'react';
import Layout from '../../components/ui/Layout';
import SubHeader from '../../components/ui/SubHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';

import userApprovalService from '../../services/userApprovalService';
import { useAuth } from '../../contexts/AuthContext';

import RegistrationFilters from './components/RegistrationFilters';
import RegistrationQueue from './components/RegistrationQueue';
import ApprovalActionsPanel from './components/ApprovalActionsPanel';

const UserRegistrationApprovalDashboard = () => {
  const { userProfile } = useAuth();
  const [approvalRequests, setApprovalRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadApprovalRequests();
  }, []);

  const loadApprovalRequests = async () => {
    try {
      setLoading(true);
      const data = await userApprovalService?.getAllApprovalRequests();
      setApprovalRequests(data);
    } catch (error) {
      console.error('Error loading approval requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    if (!userProfile?.id) {
      alert('يجب تسجيل الدخول كمسؤول');
      return;
    }

    try {
      setLoading(true);
      await userApprovalService?.approveUser(userId, userProfile?.id);
      await loadApprovalRequests();
      alert('تم الموافقة على المستخدم بنجاح');
      setSelectedUser(null);
    } catch (error) {
      console.error('Error approving user:', error);
      alert('فشل في الموافقة على المستخدم: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectClick = (user) => {
    setSelectedUser(user);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason?.trim()) {
      alert('يرجى إدخال سبب الرفض');
      return;
    }

    if (!userProfile?.id) {
      alert('يجب تسجيل الدخول كمسؤول');
      return;
    }

    try {
      setLoading(true);
      await userApprovalService?.rejectUser(selectedUser?.id, userProfile?.id, rejectionReason);
      await loadApprovalRequests();
      setShowRejectModal(false);
      setSelectedUser(null);
      alert('تم رفض المستخدم بنجاح');
    } catch (error) {
      console.error('Error rejecting user:', error);
      alert('فشل في رفض المستخدم: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      admin: 'مدير النظام',
      property_manager: 'مدير الخصائص',
      contract_specialist: 'أخصائي العقود',
      client_manager: 'مدير العملاء',
      agent: 'وكيل',
      manager: 'مدير',
      staff: 'موظف'
    };
    return roleLabels?.[role] || role;
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      pending_approval: 'قيد المراجعة',
      approved: 'موافق عليه',
      rejected: 'مرفوض'
    };
    return statusLabels?.[status] || status;
  };

  const filterByDateRange = (request) => {
    if (filterDateRange === 'all') return true;
    const createdDate = new Date(request?.created_at);
    const now = new Date();
    const daysDiff = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));

    switch (filterDateRange) {
      case 'today':
        return daysDiff === 0;
      case 'week':
        return daysDiff <= 7;
      case 'month':
        return daysDiff <= 30;
      default:
        return true;
    }
  };

  const filteredRequests = approvalRequests?.filter(request => {
    const statusMatch = filterStatus === 'all' || request?.approval_status === filterStatus;
    const roleMatch = filterRole === 'all' || request?.role === filterRole;
    const dateMatch = filterByDateRange(request);
    const searchMatch = !searchQuery || 
      request?.full_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      request?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    return statusMatch && roleMatch && dateMatch && searchMatch;
  });

  const handleBulkApprove = async () => {
    const pendingUsers = filteredRequests?.filter(r => r?.approval_status === 'pending_approval');
    if (pendingUsers?.length === 0) {
      alert('لا توجد طلبات معلقة للموافقة الجماعية');
      return;
    }

    if (!confirm(`هل أنت متأكد من الموافقة على ${pendingUsers?.length} طلب؟`)) {
      return;
    }

    try {
      setLoading(true);
      for (const user of pendingUsers) {
        await userApprovalService?.approveUser(user?.id, userProfile?.id);
      }
      await loadApprovalRequests();
      alert('تم الموافقة على جميع الطلبات بنجاح');
    } catch (error) {
      console.error('Error bulk approving:', error);
      alert('فشل في الموافقة الجماعية: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['الاسم', 'البريد الإلكتروني', 'الدور', 'الحالة', 'تاريخ التسجيل'],
      ...filteredRequests?.map(r => [
        r?.full_name,
        r?.email,
        getRoleLabel(r?.role),
        getStatusLabel(r?.approval_status),
        new Date(r?.created_at)?.toLocaleDateString('ar-SA')
      ])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `user_registrations_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    link?.click();
  };

  const pendingCount = approvalRequests?.filter(r => r?.approval_status === 'pending_approval')?.length;
  const approvedCount = approvalRequests?.filter(r => r?.approval_status === 'approved')?.length;
  const rejectedCount = approvalRequests?.filter(r => r?.approval_status === 'rejected')?.length;

  return (
    <Layout userRole={userProfile?.role} userName={userProfile?.full_name} onQuickMenuToggle={() => {}}>
      <SubHeader pageTitle="لوحة موافقات التسجيل" />
      <div className="min-h-screen bg-background p-4 md:p-6" dir="rtl">
        {/* Header */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Icon name="UserCheck" size={32} color="#3B82F6" />
              لوحة موافقات تسجيل المستخدمين
            </h1>
            <p className="text-muted-foreground mt-2">
              إدارة طلبات التسجيل المعلقة مع الموافقة السريعة والمراجعة الشاملة
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={loadApprovalRequests}
              variant="outline"
              iconName="RefreshCw"
              disabled={loading}
            >
              تحديث
            </Button>
            <Button
              onClick={handleExport}
              variant="outline"
              iconName="Download"
            >
              تصدير
            </Button>
            <Button
              onClick={handleBulkApprove}
              variant="default"
              iconName="CheckCheck"
              disabled={pendingCount === 0 || loading}
            >
              موافقة جماعية ({pendingCount})
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">طلبات معلقة</p>
                <p className="text-3xl font-bold text-yellow-400 mt-2">{pendingCount}</p>
                <p className="text-xs text-muted-foreground mt-1">تحتاج إلى مراجعة</p>
              </div>
              <div className="p-4 bg-yellow-500/20 rounded-lg">
                <Icon name="Clock" size={32} color="#FBBF24" />
              </div>
            </div>
          </div>

          <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">موافق عليهم</p>
                <p className="text-3xl font-bold text-green-400 mt-2">{approvedCount}</p>
                <p className="text-xs text-muted-foreground mt-1">حسابات نشطة</p>
              </div>
              <div className="p-4 bg-green-500/20 rounded-lg">
                <Icon name="CheckCircle" size={32} color="#10B981" />
              </div>
            </div>
          </div>

          <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">مرفوضين</p>
                <p className="text-3xl font-bold text-red-400 mt-2">{rejectedCount}</p>
                <p className="text-xs text-muted-foreground mt-1">طلبات مرفوضة</p>
              </div>
              <div className="p-4 bg-red-500/20 rounded-lg">
                <Icon name="XCircle" size={32} color="#EF4444" />
              </div>
            </div>
          </div>
        </div>

        {/* Three-Panel Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Sidebar - Filters (25%) */}
          <div className="lg:col-span-3">
            <RegistrationFilters
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
              filterRole={filterRole}
              setFilterRole={setFilterRole}
              filterDateRange={filterDateRange}
              setFilterDateRange={setFilterDateRange}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              pendingCount={pendingCount}
            />
          </div>

          {/* Center - Registration Queue (60%) */}
          <div className="lg:col-span-6">
            <RegistrationQueue
              requests={filteredRequests}
              loading={loading}
              selectedUser={selectedUser}
              onUserSelect={handleUserSelect}
              onApprove={handleApprove}
              onReject={handleRejectClick}
              getRoleLabel={getRoleLabel}
            />
          </div>

          {/* Right Panel - Approval Actions (15%) */}
          <div className="lg:col-span-3">
            <ApprovalActionsPanel
              selectedUser={selectedUser}
              onApprove={handleApprove}
              onReject={handleRejectClick}
              getRoleLabel={getRoleLabel}
            />
          </div>
        </div>
      </div>
      {/* Rejection Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="رفض طلب التسجيل"
        footer={null}
        icon={null}
        iconColor={null}
      >
        <div className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              المستخدم: <span className="text-white font-medium">{selectedUser?.full_name}</span>
            </p>
            <p className="text-sm text-muted-foreground">
              البريد الإلكتروني: <span className="text-white font-medium">{selectedUser?.email}</span>
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              سبب الرفض <span className="text-red-400">*</span>
            </label>
            <select
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e?.target?.value)}
              className="w-full px-4 py-2 bg-card border border-border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">اختر سبب الرفض</option>
              <option value="معلومات غير كاملة">معلومات غير كاملة</option>
              <option value="بيانات غير صحيحة">بيانات غير صحيحة</option>
              <option value="عدم استيفاء الشروط">عدم استيفاء الشروط</option>
              <option value="حساب مكرر">حساب مكرر</option>
              <option value="طلب غير مصرح به">طلب غير مصرح به</option>
              <option value="أخرى">أخرى</option>
            </select>
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <Button
              onClick={() => setShowRejectModal(false)}
              variant="outline"
            >
              إلغاء
            </Button>
            <Button
              onClick={handleRejectConfirm}
              variant="destructive"
              disabled={!rejectionReason || loading}
            >
              تأكيد الرفض
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default UserRegistrationApprovalDashboard;